/**
 * Tracen striimaus kehystävälle editorille (WP-DT3,
 * app-debug-timeline-plan.md §4.3).
 *
 * <p>Vastinpari editorin {@link ../editor/traceHost}ille. Erillään
 * {@link ./bridge}stä samasta syystä kuin {@link ./theme}: datasilta on
 * pyyntö/vastaus-protokolla korrelaatio-id:llä, tämä on yksisuuntainen virta
 * ilmoituksia.
 *
 * <p><b>Vaikenee kunnes editori pyytää.</b> Tracer nauhoittaa metadataa aina
 * (päätös `D2`), mutta viestiliikennettä ei synny ennen kuin parent lähettää
 * `trace-control`-viestin. Portaalin runner ei pyydä, joten se ei posta mitään.
 *
 * <p><b>Ei koskaan serving-dokumentissa</b> (upotussivun-tapahtumaviesti-plan.md
 * WP-UV7). Julkaistun Appin standalone-shell voi olla upotettuna vieraalle
 * sivustolle (WP-EM3), jolloin parent on asiakkaan sivu eikä editori. Virtaa ei
 * silloin kytketä lainkaan: muuten isäntäsivu saisi yhdellä viestillä kaikki
 * tapahtumat arvoineen. Serving-tilassa trace luetaan F12-konsolista
 * (`window.fasthub._trace`).
 *
 * <p><b>Nauhoitus = full-moodi.</b> Sama kytkin hoitaa molemmat: kun editori
 * nauhoittaa, myös slot-arvot otetaan talteen. Vastaanottaja on tekijän oma
 * paneeli (yllä oleva rajaus takaa sen), joten erillistä "striimaa muttei
 * arvoja" -tilaa ei tarvita.
 */

import { isServingDocument } from './bridge';
import type { TraceEvent, Tracer } from './trace';

/** Editorilta tuleva ohjausviesti. */
export interface TraceControlMessage {
  type: 'fasthub:app:trace-control';
  recording: boolean;
}

/** Appista lähtevä eräviesti. */
export interface TraceBatchMessage {
  type: 'fasthub:app:trace';
  events: TraceEvent[];
}

export const TRACE_CONTROL_TYPE = 'fasthub:app:trace-control';
export const TRACE_BATCH_TYPE = 'fasthub:app:trace';

/**
 * Liitä striimi. Palauttaa irrotusfunktion; no-op (ja irrotus no-op) kun sivu
 * ei ole kehystetty tai kun se on serving-dokumentti (WP-UV7).
 */
export function attachTraceStream(tracer: Tracer, win: Window = window): () => void {
  if (win.parent === win) return () => {};
  // Kuuntelijaa ei edes kiinnitetä: serving-dokumentissa trace-control on
  // vieraan sivun viesti, eikä sille ole vastaanottajaa jonka kuulua saada virta.
  if (isServingDocument(win.document)) return () => {};
  const parent = win.parent;
  let unsubscribe: (() => void) | null = null;

  const post = (events: TraceEvent[]): void => {
    if (events.length === 0) return;
    try {
      const message: TraceBatchMessage = { type: TRACE_BATCH_TYPE, events };
      parent.postMessage(message, '*');
    } catch {
      // Kloonaamaton arvo tai suljettu ikkuna: debuggerin virhe ei saa
      // kaataa debugattavaa Appia.
    }
  };

  const startRecording = (): void => {
    if (unsubscribe) return;
    tracer.setMode('full');
    // Puskurin nykyinen sisältö ensimmäisenä eränä: paneeli näyttää heti
    // sivun latausketjun eikä jää tyhjäksi siihen asti että käyttäjä ehtii
    // tehdä jotain. Nämä ovat metadata-moodissa kirjattuja (ei arvoja) —
    // full-moodi koskee vasta tästä eteenpäin kirjattavia.
    post(tracer.events.map((e) => ({ ...e })));
    unsubscribe = tracer.subscribe(post);
  };

  const stopRecording = (): void => {
    unsubscribe?.();
    unsubscribe = null;
    tracer.setMode('metadata');
  };

  const handler = (e: MessageEvent) => {
    // Vain omalta parentilta (sama e.source-tarkistus kuin datasillassa).
    if (e.source !== parent) return;
    const data = e.data as Partial<TraceControlMessage> | null;
    if (!data || data.type !== TRACE_CONTROL_TYPE) return;
    if (data.recording === true) startRecording();
    else stopRecording();
  };

  win.addEventListener('message', handler);
  return () => {
    win.removeEventListener('message', handler);
    stopRecording();
  };
}
